import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DkSelect, DkSelectOptionTemplate, type DkSelectOption } from '@dkds/ng-components';

/** A project as the dashboard's selector lists it. */
export interface DashboardProjectOption {
  id: string;
  name: string;
  logo: string | null;
  color: string | null;
}

interface ProjectSelectOption extends DkSelectOption {
  logo: string | null;
  color: string | null;
  initial: string;
}

/** Past this many projects the list can be searched. */
const SEARCHABLE_FROM = 7;

/**
 * The Beta Dashboard's project picker, drawn in the header's breadcrumb (Figma `9669:59673`):
 * one project at a time, its letter or logo beside its name.
 *
 * Built on `dk-select`, the single-select the New Request drawer uses. Independent of the navbar's
 * multi-project selector: no pins, no multi-selection, nothing shared with it. Presentational —
 * it emits every pick, the current project included, and the page decides what that means.
 */
@Component({
  selector: 'app-dashboard-project-select',
  standalone: true,
  imports: [FormsModule, DkSelect, DkSelectOptionTemplate],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <dk-select
      size="sm"
      ariaLabel="Project"
      emptyText="No projects found"
      searchPlaceholder="Search projects"
      [searchable]="options().length >= searchableFrom"
      [options]="options()"
      [ngModel]="value()"
      (selectionChange)="onSelect($event)"
    >
      <ng-template [dkSelectOption]="options()" let-project let-inField="inField">
        <span class="project-select__row" [class.project-select__row--field]="inField">
          <span
            class="dk-avatar dk-avatar--xs project-select__mark"
            [style.--project-color]="project.logo ? null : project.color"
            aria-hidden="true"
          >
            @if (project.logo) {
              <img [src]="project.logo" alt="" />
            } @else {
              {{ project.initial }}
            }
          </span>
          <span class="project-select__name">{{ project.label }}</span>
        </span>
      </ng-template>
    </dk-select>
  `,
  styles: `
    @use '@dkds/core/tokens' as dk;

    :host {
      display: inline-flex;
    }

    // In a breadcrumb the field hugs its content instead of filling its container, and is as thin
    // as the frame's 28px trigger: the small select's two 32px minimums would make it ~42px.
    :host ::ng-deep .dk-select {
      inline-size: auto;
      min-block-size: 0;
      padding-block: 0;
    }

    :host ::ng-deep .dk-select__toggle {
      min-block-size: 0;
      // 3px above and below the 20px mark, plus the border: 28px, as in Figma 9669:59673.
      padding-block: calc(#{dk.$space-2xs} * 0.75);
    }

    .project-select__row {
      display: inline-flex;
      align-items: center;
      gap: dk.$space-xs;
    }

    .project-select__row--field .project-select__name {
      font-size: dk.$text-sm;
      font-weight: dk.$font-weight-semibold;
      color: dk.$color-foreground;
    }

    .project-select__mark {
      border-radius: dk.$radius-xs;
      // The colour is the project's own, chosen by a person, so it is data rather than a token.
      background: var(--project-color, #{dk.$color-primary});
      color: dk.$color-primary-foreground;
    }
  `,
})
export class DashboardProjectSelectComponent {
  readonly projects = input.required<readonly DashboardProjectOption[]>();
  /** The open project's id. */
  readonly value = input.required<string>();

  /** The project picked, which may be the one already open. */
  readonly selected = output<string>();

  protected readonly searchableFrom = SEARCHABLE_FROM;

  protected readonly options = computed<ProjectSelectOption[]>(() =>
    this.projects().map((project) => ({
      value: project.id,
      label: project.name,
      logo: project.logo || null,
      color: project.color || null,
      initial: project.name.trim().charAt(0).toUpperCase(),
    })),
  );

  protected onSelect(option: ProjectSelectOption | null): void {
    if (option) this.selected.emit(option.value);
  }
}
